import { setPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { Hub } from 'interfaces/Home';
import type { GetStaticProps } from 'next/types';
import { educationLayoutCounts, Microsites } from 'constants/constants';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import getSection from 'api/Section/getSection';
import {
  type Disclosure,
  getMicrositeConfig,
  type MicrositeConfig,
  type SectionsConfig,
} from 'api/MicrositeConfig/getMicrositeConfig';
import { recommendsLogo } from 'icons/RecommendsLogo';
import type { HeaderNav } from 'interfaces/navigation/Header';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import SectionPage from 'pages/SectionPage';
import type { RecentArticles, PinnedArticle } from 'interfaces/content/articles/Articles';
import { populateThumbnailToMenuItems } from 'api/Topic/getTopicMenuItemThumbnails';
import { getEduRankings } from 'api/EduRanking/getEduRankings';
import type { EduRankings } from 'interfaces/EduRanking';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import log from 'utils/log/logger';
import getFooter from 'api/Footer/getFooter';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallback from 'lib/fallbackData/commonData';
import recommendsFallback from 'lib/fallbackData/recommendsData';
import setEducationPageAdConfig from 'services/Ad/config/setEducationPageAdConfig';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

export interface SectionHubProps {
  section: Hub;
  latestArticles: RecentArticles;
  pinnedArticles: PinnedArticle[];
  sectionsConfig?: SectionsConfig;
  widgets?: { [key: string]: string };
  subsectionsLatest?: Hub[];
  rankings?: EduRankings;
  pagination: PagePagination;
}

const EducationSectionHub = ({
  section,
  sectionsConfig,
  latestArticles,
  pinnedArticles,
  widgets,
  subsectionsLatest,
  rankings,
  pagination,
}: SectionHubProps) => (
  <SectionPage
    section={section}
    pagination={pagination}
    sectionsConfig={sectionsConfig}
    latestArticles={latestArticles}
    pinnedArticles={pinnedArticles}
    platform={Microsites.Education}
    widgets={widgets}
    subsectionsLatest={subsectionsLatest}
    eduRankings={rankings}
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);

    if (pageNum === '1' || Number.isNaN(currentPageNum) || currentPageNum < 1) {
      throw new NotFoundError(`[getEducationSectionPageNumber] invalid page number: '${currentPageNum}'`);
    }

    const sectionSlug = 'education';
    const footer = await safeAsync(() => getFooter(Microsites.Recommends), fallback.footer);
    const menu: HeaderNav = await safeAsync(
      () =>
        process.env.FEATURE_FLAG_DISABLE_RECOMMENDS
          ? getMenu(
              'LEFT_MENU_COM',
              'RIGHT_MENU_COM',
              'TOPIC_NAVIGATION_COM',
              Microsites.Recommends,
              null,
              'RIGHT_MENU_COM',
            )
          : getMenu(
              'LEFT_MENU_RECOMMENDS',
              'LEFT_MENU_COM',
              'TOPIC_NAVIGATION_RECOMMENDS_V2',
              Microsites.Recommends,
              recommendsLogo,
              'RIGHT_MENU_COM',
            ),
      recommendsFallback.headerNav,
    );
    await safe(() => populateThumbnailToMenuItems(menu.topicNavigation), null);

    const educationData: MicrositeConfig = await safeAsync(
      () => getMicrositeConfig(Microsites.Education),
      recommendsFallback.recommendsData,
    );

    const disclosure: Disclosure | undefined | null = educationData.disclosure || null;
    const educationSections: SectionsConfig = educationData.sections || ({} as SectionsConfig);

    const section = (await safeAsync(
      () => getSection(sectionSlug, 0, 0, Microsites.Education, educationSections),
      recommendsFallback.section,
    )) as Hub;

    const pageAdConfig = setEducationPageAdConfig(section);

    const excludedIds: number[] = [];

    const subsectionsLatest = [] as Hub[];

    const debuggingLabel = 'eduSectionDebugging';

    const subSectionsResponse = await Promise.allSettled(
      (section.children || []).map(async (subSection) => {
        try {
          const sectionData = await getSection(
            subSection.slug,
            educationLayoutCounts.itemsPerSectionPreview,
            0,
            Microsites.Education,
          );

          const subSectionSlug = Object.keys(educationSections).find(
            (key) => educationSections[key].slug === subSection.slug,
          );

          return {
            ...sectionData,
            link: subSectionSlug
              ? sectionData?.link?.replace(`/${sectionData?.slug}/`, `/${subSectionSlug}/`)
              : sectionData?.link,
            slug: subSectionSlug || '',
          };
        } catch (err) {
          log.error(debuggingLabel, { error: (err as Error).stack });
          throw err;
        }
      }),
    );
    const updatedChildren: Array<{ name: string; link: string; slug: string }> = [];

    subSectionsResponse.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        const subsectionData = result.value as Hub;
        if (subsectionData.posts) {
          subsectionsLatest.push(subsectionData);
          excludedIds.push(...subsectionData.posts.map((post) => post.databaseId || 0));
        }

        // Rebuild Children array with updated section slug & link
        updatedChildren.push({
          ...subsectionData,
        });
      }
    });
    section.children = updatedChildren;

    // fetch career hub latest articles
    const careerSlug = 'careers';
    const careerSectionData = await safeAsync(
      () => getSection(careerSlug, 4, 0, Microsites.Education),
      recommendsFallback.section,
    );
    if (careerSectionData && careerSectionData.posts) {
      subsectionsLatest.push(careerSectionData);
      excludedIds.push(...careerSectionData.posts.map((post) => post?.databaseId || 0));
    }

    const rankings = await safeAsync(() => getEduRankings(), recommendsFallback.rankingsObj);
    const dataLayer = safe(
      () => setPageGtmConfig({ ...section, platformCD: Microsites.Education }),
      fallback.dataLayer,
    );

    const getLatestArticles = () =>
      getSection(
        sectionSlug,
        educationLayoutCounts.educationSectionLatest,
        (currentPageNum - 1) * educationLayoutCounts.educationSectionLatest,
        Microsites.Education,
        undefined,
        undefined,
        excludedIds,
      );

    // Fetch articles for 'Latest Articles' section excluding articles already displayed
    const latestArticles = (
      currentPageNum === 1 ? await safeAsync(getLatestArticles, recommendsFallback.section) : await getLatestArticles()
    ) as Hub;

    if (currentPageNum > 1 && currentPageNum > latestArticles.pageInfo.totalCount) {
      throw new NotFoundError(`[getEducationSectionPageNumber] invalid page number: '${currentPageNum}'`);
    }

    const pagination = calculatePagePagination(
      currentPageNum,
      latestArticles.pageInfo.totalCount,
      educationLayoutCounts.educationSectionLatest,
      'section',
    );

    const headData = safe(
      () =>
        setHeadData(
          {
            ...section,
            canonicalUrl:
              currentPageNum && currentPageNum > 1
                ? `${section.canonicalUrl}page/${currentPageNum}/`
                : section.canonicalUrl,
            platformCD: Microsites.Education,
            titlePrefix: pagination.pageInfo,
          },
          true,
          'Fortune Education',
        ),
      fallback.headData,
    );
    const subMenu = menu?.topicNavigation.find((menuItem) => menuItem.label === section.name)?.subMenu || null;
    return {
      props: {
        dataLayer,
        footer,
        headData,
        headerNav: { ...menu, disclosure },
        latestArticles,
        pageAdConfig,
        pagination,
        pianoPageConfig: setPianoConfig({ pageType: PianoExperiencePageType.Default, pageData: {} }),
        rankings,
        section,
        sectionsConfig: educationSections,
        subMenu,
        subsectionsLatest,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export default EducationSectionHub;
